import React, { useCallback, useRef } from 'react'
import CharterBundleCalendarModal from './CharterBundleCalendarModal'
import type { FlightsClassTypes } from 'constants/flight'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { DateRange } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/types'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import OfferCalendarDatePickerSelectBox from '../CalendarPicker/OfferCalendarDatePickerSelectBox'

interface Props {
  selectedMonth: string
  onMonthChange: (month: string) => void
  nights: number
  fareClass: FlightsClassTypes
  onDurationChange: (nights: number) => void
  durationOptions: ReadonlyArray<{ value: number, label: string }>
  checkInDate?: string
  charterBundleRoute?: App.CharterBundleRoute
  rooms: Array<OfferPageRoom>
  className?: string
  dateRangesByMonth: Record<string, Array<DateRange>>
  open: boolean
}

/**
 * Charter Flight Calendar Select for Desktop
 * Uses ModalContext pattern to open the calendar modal
 */
function CharterFlightCalendarSelect(props: Props) {
  const {
    selectedMonth,
    onMonthChange,
    onDurationChange,
    durationOptions,
    className,
    dateRangesByMonth,
    open,
  } = props

  const dispatch = useOfferPageDispatch()
  const isMobile = useScreenSizeOnly('mobile')

  const onClose = useCallback(() => {
    dispatch({ type: OfferPageActions.setRoomType, room: undefined })
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dispatch])

  const handleFareClassChange = useCallback((selectedClass: FlightsClassTypes) => {
    dispatch({ type: OfferPageActions.setFareClass, fareClass: selectedClass })
  }, [dispatch])

  const handleDateSelect = useCallback((option: { departDate: string, returnDate: string, checkIn: string, checkOut: string }) => {
    // Update dates in offer page state
    dispatch({
      type: OfferPageActions.setDates,
      checkIn: option.checkIn,
      checkOut: option.checkOut,
    })
    // Close filter after selecting dates
    onClose()
  }, [dispatch, onClose])

  const triggerRef = useRef<HTMLButtonElement>(null)

  return (<div className={className}>
    <OfferCalendarDatePickerSelectBox
      ref={triggerRef}
    />
    <CharterBundleCalendarModal
      isOpen={open && !isMobile}
      onClose={onClose}
      onDateSelect={handleDateSelect}
      selectedMonth={selectedMonth}
      onMonthChange={onMonthChange}
      onDurationChange={onDurationChange}
      durationOptions={durationOptions}
      onFareClassChange={handleFareClassChange}
      dateRangesByMonth={dateRangesByMonth}
    />
  </div>
  )
}

export default withOfferPageState(state => ({
  open: state.activeFilter === 'checkin',
}), CharterFlightCalendarSelect)
